import clsx from 'clsx';
import { useContext } from 'react';

import {
  insideLabelPositioner,
  insideZoneLabel,
  insideZoneOverlay,
  interactiveLabel,
  leftZoneLabel,
  rightZoneLabel,
  sideLabelContainer,
  sideLabelPositioner,
} from './SankeyLabelOverlay.sty.js';
import { Tooltip } from '../../../overlays/tooltip/Tooltip.js';
import type { TruncationMode } from '../../../typography/text-ellipsis/TextEllipsis.js';
import { charsFontWidth14pixelsSize500Map } from '../../core/components/text-ellipsis/chars-font-width-map.js';
import {
  calculateWidth,
  truncateString,
} from '../../core/components/text-ellipsis/truncate-string.js';
import {
  INSIDE_LABEL_PADDING,
  INSIDE_LABEL_WIDTH_RATIO,
} from '../constants.js';
import { SankeyTruncationModeContext } from '../context/SankeyTruncationMode.context.js';
import type {
  SankeyLayoutModel,
  SankeyLayoutNode,
} from '../types/sankey-internal.js';

type SankeyLabelZone = 'left' | 'inside' | 'right';

type NodeWithGeometry = SankeyLayoutNode & {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
};

function hasGeometry(node: SankeyLayoutNode): node is NodeWithGeometry {
  return (
    node.x0 !== undefined &&
    node.x1 !== undefined &&
    node.y0 !== undefined &&
    node.y1 !== undefined
  );
}

interface SankeyLabelOverlayProps {
  zone: SankeyLabelZone;
  layout: SankeyLayoutModel;
  maxWidth?: number;
}

function getVisualColumns(nodes: NodeWithGeometry[]): number[] {
  return [...new Set(nodes.map((n) => n.x0))].sort((a, b) => a - b);
}

function getInsideLabelMaxWidth(
  node: NodeWithGeometry,
  visualColumns: number[],
): number {
  const nextColX0 = visualColumns.find((x) => x > node.x0) ?? node.x1;
  return Math.max(0, (nextColX0 - node.x1) * INSIDE_LABEL_WIDTH_RATIO);
}

function getMaxLabelWidth(columnNodes: SankeyLayoutNode[]): number {
  let max = 0;
  for (const node of columnNodes) {
    const w = calculateWidth(
      node.label ?? '',
      charsFontWidth14pixelsSize500Map,
    );
    if (w > max) {
      max = w;
    }
  }
  return Math.ceil(max);
}

function renderSideLabels(
  columnNodes: NodeWithGeometry[],
  labelClassName: string,
  truncationMode: TruncationMode,
  maxWidth?: number,
) {
  const measuredWidth = getMaxLabelWidth(columnNodes);
  const effectiveWidth =
    maxWidth !== undefined && maxWidth > 0
      ? Math.min(measuredWidth, maxWidth)
      : measuredWidth;

  return (
    <div className={sideLabelContainer} style={{ width: effectiveWidth }}>
      {columnNodes.map((node) => {
        const fullLabel = node.label ?? '';
        const label = truncateString(
          fullLabel,
          effectiveWidth,
          charsFontWidth14pixelsSize500Map,
          truncationMode,
        );
        const isTruncated = label !== fullLabel;
        return (
          <div
            key={node.id}
            className={sideLabelPositioner}
            style={{ top: node.y0, height: node.y1 - node.y0 }}
          >
            <Tooltip text={fullLabel} disabled={!isTruncated}>
              <span
                className={clsx(
                  labelClassName,
                  isTruncated && interactiveLabel,
                )}
              >
                {label}
              </span>
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
}

export function SankeyLabelOverlay({
  zone,
  layout,
  maxWidth,
}: Readonly<SankeyLabelOverlayProps>) {
  const truncationMode = useContext(SankeyTruncationModeContext);
  const validNodes = layout.nodes.filter(hasGeometry);

  if (validNodes.length === 0) {
    return null;
  }

  const visualColumns = getVisualColumns(validNodes);
  const leftX0 = visualColumns[0];
  const rightX0 = visualColumns[visualColumns.length - 1];
  const hasMultipleColumns = visualColumns.length > 1;

  if (zone === 'left') {
    const columnNodes = validNodes.filter((n) => n.x0 === leftX0);
    if (columnNodes.length === 0) {
      return null;
    }
    return renderSideLabels(
      columnNodes,
      leftZoneLabel,
      truncationMode,
      maxWidth,
    );
  }

  if (zone === 'right') {
    if (!hasMultipleColumns) {
      return null;
    }
    const lastColumnNodes = validNodes.filter((n) => n.x0 === rightX0);
    if (lastColumnNodes.length === 0) {
      return null;
    }
    return renderSideLabels(
      lastColumnNodes,
      rightZoneLabel,
      truncationMode,
      maxWidth,
    );
  }

  const middleNodes = validNodes.filter(
    (n) => n.x0 !== leftX0 && (!hasMultipleColumns || n.x0 !== rightX0),
  );

  if (middleNodes.length === 0) {
    return null;
  }

  return (
    <div className={insideZoneOverlay}>
      {middleNodes.map((node) => {
        const nodeHeight = node.y1 - node.y0;
        const insideWidth = getInsideLabelMaxWidth(node, visualColumns);
        const fullLabel = node.label ?? '';
        const label = truncateString(
          fullLabel,
          insideWidth,
          charsFontWidth14pixelsSize500Map,
          truncationMode,
        );
        const isTruncated = label !== fullLabel;

        return (
          <div
            key={node.id}
            className={insideLabelPositioner}
            style={{
              top: node.y0,
              height: nodeHeight,
              left: node.x1 + INSIDE_LABEL_PADDING,
              maxWidth: insideWidth,
            }}
          >
            <Tooltip text={fullLabel} disabled={!isTruncated}>
              <span
                className={clsx(
                  insideZoneLabel,
                  isTruncated && interactiveLabel,
                )}
              >
                {label}
              </span>
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
}

SankeyLabelOverlay['displayName'] = 'SankeyLabelOverlay';
